import { useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, Loader2, Plus, Sparkles, Wand2 } from 'lucide-react';
import type { CourseShape } from '@eduborg/shared';
import { AppHeader } from '@/components/AppHeader';
import { SparkModal } from '@/components/spark/SparkModal';
import { Field } from '@/components/ui/Field';
import { useAuth } from '@/lib/auth-store';
import { ApiError } from '@/lib/api';
import { useCoursesQuery, useCreateCourseMutation } from '@/hooks/queries/useCourses';
import { cn } from '@/lib/utils';

export function Workspace() {
  const { state } = useAuth();
  if (state.status !== 'authenticated') return null;
  const isStudent = state.profile.accountType === 'student';
  return <WorkspaceInner token={state.token} isStudent={isStudent} />;
}

function WorkspaceInner({ token, isStudent }: { token: string; isStudent: boolean }) {
  const coursesQuery = useCoursesQuery(token);
  const [creating, setCreating] = useState(false);
  const [sparking, setSparking] = useState(false);

  const courses = coursesQuery.data?.courses ?? [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(220,40%,98%)] via-background to-secondary/40">
      <AppHeader />

      <main className="container py-10">
        <header className="flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">
              {isStudent ? 'My classes' : 'Workspace'}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {isStudent
                ? 'Every class you’ve joined. Click one to open it.'
                : 'Where you build, run, and iterate on your courses.'}
            </p>
          </div>
          {!isStudent && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setSparking(true)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-accent/30 bg-accent/5 px-4 py-2.5 text-sm font-semibold text-accent transition-colors hover:bg-accent/10"
              >
                <Sparkles className="h-4 w-4" aria-hidden /> Spark a course
              </button>
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:translate-y-[-1px] hover:shadow-lg"
              >
                <Plus className="h-4 w-4" aria-hidden /> New course
              </button>
            </div>
          )}
        </header>

        <section className="mt-8">
          {coursesQuery.isLoading ? (
            <CoursesSkeleton />
          ) : coursesQuery.isError ? (
            <CoursesError onRetry={() => coursesQuery.refetch()} />
          ) : courses.length === 0 ? (
            <EmptyState onCreate={() => setCreating(true)} isStudent={isStudent} />
          ) : (
            <CourseGrid courses={courses} isStudent={isStudent} />
          )}
        </section>
      </main>

      {creating && (
        <NewCourseDialog
          token={token}
          onClose={() => setCreating(false)}
          onSuccess={() => setCreating(false)}
        />
      )}

      {sparking && <SparkModal token={token} onClose={() => setSparking(false)} />}
    </div>
  );
}

/* ─── Course grid ──────────────────────────────────────────────────────── */

function CourseGrid({ courses, isStudent }: { courses: CourseShape[]; isStudent: boolean }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {courses.map((c, i) => (
        <motion.a
          key={c.id}
          href={`/c/${c.orgSlug}/${c.slug}`}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: i * 0.04 }}
          className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-xl"
        >
          <span
            aria-hidden
            className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-accent/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
          />
          <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-accent">
            <BookOpen className="h-4 w-4" aria-hidden />
          </span>
          <h3 className="relative mt-4 font-display text-base font-semibold">{c.title}</h3>
          <p className="relative mt-1 line-clamp-2 text-sm text-muted-foreground">
            {c.description ?? 'No description yet.'}
          </p>
          {/* Status pill + join-code are instructor-only — students see neither
              on the card (mirroring the CourseDetail header rule). */}
          {!isStudent && (
            <div className="relative mt-3 flex items-center justify-between text-xs text-muted-foreground">
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 font-medium uppercase tracking-wider',
                  c.status === 'active'
                    ? 'bg-accent/10 text-accent'
                    : c.status === 'archived'
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-secondary text-foreground',
                )}
              >
                {c.status}
              </span>
              <span>
                Code: <span className="font-mono">{c.joinCode}</span>
              </span>
            </div>
          )}
        </motion.a>
      ))}
    </div>
  );
}

/* ─── Empty + loading + error states ───────────────────────────────────── */

function EmptyState({ onCreate, isStudent }: { onCreate: () => void; isStudent: boolean }) {
  return (
    <div className="rounded-3xl border border-dashed border-border bg-card/50 p-12 text-center">
      <img
        src="/brand/eduborg-mascot.png"
        alt=""
        aria-hidden
        width={72}
        height={72}
        className="mx-auto animate-mascot-idle"
      />
      <h2 className="mt-4 font-display text-xl font-semibold">
        {isStudent ? 'No classes yet.' : 'No courses yet.'}
      </h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        {isStudent
          ? 'Got a 6-character code from your teacher? Use it to join your first class.'
          : 'Spin one up — EduBorg can scaffold the structure and the first lesson in under a minute.'}
      </p>
      {isStudent ? (
        <a
          href="/join"
          className="mt-6 inline-flex items-center gap-1.5 rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground shadow transition-shadow hover:shadow-lg"
        >
          Join a class
        </a>
      ) : (
        <button
          type="button"
          onClick={onCreate}
          className="mt-6 inline-flex items-center gap-1.5 rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground shadow transition-shadow hover:shadow-lg"
        >
          <Wand2 className="h-4 w-4" aria-hidden /> Create your first course
        </button>
      )}
    </div>
  );
}

function CoursesSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="h-44 animate-pulse rounded-2xl border border-border bg-card/60"
          aria-hidden
        />
      ))}
    </div>
  );
}

function CoursesError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-sm">
      <p className="font-medium text-destructive">EduBorg couldn&apos;t load your courses.</p>
      <p className="mt-1 text-muted-foreground">
        Could be a connection blip. Try again — if it keeps happening, tell us.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-medium hover:bg-secondary"
      >
        Retry
      </button>
    </div>
  );
}

/* ─── New-course dialog ────────────────────────────────────────────────── */

function suggestSlug(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

function NewCourseDialog({
  token,
  onClose,
  onSuccess,
}: {
  token: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useCreateCourseMutation(token);

  // Slug auto-suggests from title (in onTitleChange) until the user edits it.
  const onTitleChange = (v: string) => {
    setTitle(v);
    if (!slugTouched) setSlug(suggestSlug(v));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await mutation.mutateAsync({
        slug,
        title: title.trim(),
        ...(description.trim() === '' ? {} : { description: description.trim() }),
      });
      onSuccess();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'slug_taken') {
        setError('That URL is already in use. Try a different one.');
      } else if (e instanceof ApiError && e.status === 403) {
        setError('You need an instructor role in this org to create courses.');
      } else if (e instanceof ApiError && e.status === 400) {
        setError('Please check the form and try again.');
      } else {
        setError("EduBorg couldn't create the course right now.");
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/40 backdrop-blur-sm sm:items-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        role="dialog"
        aria-label="Create a new course"
        className="w-full max-w-md overflow-hidden rounded-t-3xl border-x border-t border-border bg-background shadow-2xl sm:rounded-3xl sm:border"
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-6 py-4">
          <div>
            <h2 className="font-display text-lg font-semibold">New course</h2>
            <p className="text-xs text-muted-foreground">
              You can change the title and description any time.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            <span aria-hidden className="text-lg leading-none">
              &times;
            </span>
          </button>
        </header>

        <form onSubmit={onSubmit} className="space-y-4 px-6 py-5">
          <Field
            label="Title"
            autoFocus
            required
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            hint="e.g. Biology 101, Algebra Foundations"
          />
          <Field
            label="URL slug"
            required
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(suggestSlug(e.target.value));
            }}
            hint={`This course will live at /c/${slug || 'your-slug'}`}
          />
          <label className="block space-y-1.5">
            <span className="block text-sm font-medium text-foreground">
              Short description <span className="text-muted-foreground">(optional)</span>
            </span>
            <textarea
              rows={3}
              maxLength={2000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="block w-full rounded-lg border border-input bg-card px-3 py-2 text-sm shadow-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/30"
            />
          </label>

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || title.trim().length === 0 || slug.length < 2}
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:translate-y-[-1px] hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-70"
            >
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              {mutation.isPending ? 'Creating…' : 'Create course'}
              {!mutation.isPending && <ArrowRight className="h-4 w-4" aria-hidden />}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
